import React, { isValidElement } from 'react';
import cx from 'classnames';

import Text from '../Text';

import baseStyles from '../BaseInput/BaseInput.module.scss';
import styles from './Toggle.module.scss';

export interface Props {
  children?: React.ReactNode;
  className?: Parameters<typeof cx>[0];
  classes?: {
    /** Classname to use in styling the toggle container's on and off states. Use this if you'd like to style elements outside the toggle switch, e.g. the labels. Both are optional and will use default styling otherwise. */
    containerClassName?: {
      on?: Parameters<typeof cx>[0];
      off?: Parameters<typeof cx>[0];
    };
    /** Classname to use in styling the toggle's on and off states. Use this to style the toggle _only_. Labels and other content outside the toggle will not be affected. Both are optional and will use default styling otherwise. */
    toggleClassName?: {
      on?: Parameters<typeof cx>[0];
      off?: Parameters<typeof cx>[0];
    };
  };
  disabled?: boolean;
  id: string;
  name?: string;

  /** Function to call when toggle value changes. */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** NOTE: Deprecated. Use `layout='reversed'`. Boolean to render the toggle to the right of the label rather than the left (default). */
  right?: boolean;
  /** Controls the state of the checkbox input that drives the Toggle. */
  value: boolean;
  /** NOTE: Deprecated, use trailingText.on. Text to show when the Toggle is set to true/on. */
  trueText?: string;
  /** NOTE: Deprecated, use trailingText.off. Text to show when the Toggle is set to false/off. */
  falseText?: string;
  /** Leading text (text to the left of the Toggle in left-to-right written languages, to the right for RTL languages). Supports a single simple text label or React Element, or can take an object with different values for "on" and "off" if the text needs to change based on the state of the toggle. */
  leadingLabel?: React.ReactNode & {
    on?: React.ReactNode;
    off?: React.ReactNode;
  };
  /** Trailing text (text to the right of the Toggle in left-to-right written languages, to the left for RTL languages). Supports a single simple text label or React Element, or can take an object with different values for "on" and "off" if the text needs to change based on the state of the toggle. */
  trailingLabel?: React.ReactNode & {
    on?: React.ReactNode;
    off?: React.ReactNode;
  };
  /** Custom text to use for the input's aria-label attribute.  */
  accessibilityLabel?: string;
  /** Place toggle  */
  layout?: 'default' | 'reversed';
}

export interface LabelProps {
  children?: React.ReactNode;
  className?: Parameters<typeof cx>[0];
}

const ToggleLabel: React.FC<LabelProps> = ({ children, className }) => (
  <Text
    className={cx(styles.toggleLabelText, className)}
    as='span'
    size={14}
    weight='bold'
    font='mono'
    casing='uppercase'
  >
    {children}
  </Text>
);

export const Toggle: React.FC<Props> = ({
  children,
  className,
  classes,
  disabled,
  id,
  layout = 'default',
  name,
  onChange,
  right,
  value,
  trueText,
  falseText,
  leadingLabel,
  trailingLabel,
  accessibilityLabel,
}) => {
  const hasChildren = (element: React.ReactNode) =>
    isValidElement(element) && Boolean(element.props.children);

  const childrenText = (children: React.ReactNode): React.ReactNode => {
    if (hasChildren(children))
      return childrenText?.((children as React.ReactElement)?.props.children);
    return children;
  };

  return (
    <div
      className={cx(
        baseStyles.container,
        styles.container,
        disabled && styles.disabled,
        (layout === 'reversed' || right) && styles.reversed,
        leadingLabel && styles.center,
        classes?.containerClassName
          ? value
            ? classes.containerClassName.on
            : classes.containerClassName.off
          : null,
        className,
      )}
    >
      <label className={cx(styles.label, value && styles.checked)} htmlFor={id}>
        <span role='presentation' className={styles.toggleContainer}>
          {/* Leading Label - Label to left side of toggle in LTR languages */}
          {leadingLabel && (
            <span className={cx(styles.textWrapper, styles.leadingTextWrapper)}>
              {leadingLabel.on && value ? (
                typeof leadingLabel.on === 'string' ? (
                  <ToggleLabel>{leadingLabel.on}</ToggleLabel>
                ) : (
                  leadingLabel.on
                )
              ) : leadingLabel.off && !value ? (
                typeof leadingLabel.off === 'string' ? (
                  <ToggleLabel>{leadingLabel.off}</ToggleLabel>
                ) : (
                  leadingLabel.off
                )
              ) : typeof leadingLabel === 'string' ? (
                <ToggleLabel>{leadingLabel}</ToggleLabel>
              ) : (
                leadingLabel
              )}
            </span>
          )}
          <div className={styles.inputAndToggleWrapper}>
            <input
              aria-label={
                accessibilityLabel ||
                `${
                  children
                    ? typeof children === 'string'
                      ? `${children} - `
                      : childrenText(children)
                    : ''
                }${
                  value
                    ? trailingLabel?.on ||
                      leadingLabel?.on ||
                      childrenText(trailingLabel) ||
                      'ON'
                    : trailingLabel?.off ||
                      leadingLabel?.off ||
                      childrenText(leadingLabel) ||
                      'OFF'
                }`
              }
              checked={value}
              className={cx(styles.input)}
              disabled={disabled}
              id={id}
              name={name}
              onChange={onChange}
              role='switch'
              type='checkbox'
            />
            <span
              className={cx(
                styles.toggle,
                classes?.toggleClassName
                  ? value
                    ? classes.toggleClassName.on
                    : classes.toggleClassName.off
                  : null,
              )}
            >
              <span className={styles.toggleKnob} />
            </span>
          </div>
          {/* Trailing Label - Label to right side of toggle in LTR languages */}
          <span className={styles.textWrapper}>
            {trailingLabel != null ? (
              trailingLabel.on && value ? (
                typeof trailingLabel.on === 'string' ? (
                  <ToggleLabel>{trailingLabel?.on}</ToggleLabel>
                ) : (
                  trailingLabel.on
                )
              ) : trailingLabel.off && !value ? (
                typeof trailingLabel.off === 'string' ? (
                  <ToggleLabel>{trailingLabel?.off}</ToggleLabel>
                ) : (
                  trailingLabel.off
                )
              ) : typeof trailingLabel === 'string' ? (
                <ToggleLabel>{trailingLabel}</ToggleLabel>
              ) : (
                trailingLabel
              )
            ) : (
              <>
                <ToggleLabel
                  className={cx(
                    !value && styles.hide,
                    disabled && styles.disabledLabel,
                  )}
                >
                  {trueText || 'ON'}
                </ToggleLabel>
                <ToggleLabel
                  className={cx(
                    styles.falseText,
                    value && styles.hide,
                    disabled && styles.disabledLabel,
                  )}
                >
                  {falseText || 'OFF'}
                </ToggleLabel>
              </>
            )}
          </span>
        </span>
        <Text as='span'>{children}</Text>
      </label>
    </div>
  );
};

Toggle.displayName = 'Toggle';
export default Toggle;
export { styles };
